import React from "react";
import dayjs from "dayjs";

import {
    CircularProgress,
    Avatar,
    Button,
    Card,
    CardContent,
    Typography,
    Stack,
    Chip,
    Box,
    IconButton,
    Input,
    TabList,
    Tab,
    Tabs,
    Tooltip,
    Divider,
} from "@mui/joy";
import Grid from "@mui/joy/Grid";
import Link from "@mui/joy/Link";

// components
import withRouter from "../../utils/withRouter";
import BaseApi from "../../utils/BaseApi";
import CommonInfoTab from "../Common/CommonInfoTab";
import withNavigate from "../../utils/withNavigate";
import CommonPaginatedTable from "../Common/CommonPaginatedTable";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import {retrieveImage} from "../../utils/misc";
import ChangePasswordModal from "./ChangePasswordModal";

// icons
import InfoIcon from "@mui/icons-material/Info";
import LogoutIcon from "@mui/icons-material/Logout";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import BadgeIcon from "@mui/icons-material/Badge";
import WorkIcon from "@mui/icons-material/Work";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";

// Компонент для отображения типа отпуска
const VacationTypeChip = ({componentObj}) => {
    if (!componentObj) {
        return <Chip variant="soft">N/A</Chip>;
    }
    const xtype_human = componentObj.xtype_human || 'N/A';
    const color = componentObj.color || 'neutral';
    return (
        <Chip color={color} variant="soft">
            {xtype_human}
        </Chip>
    );
};

// Компонент для отображения замещающего
const ReplacementSubscriberDisplay = ({componentObj}) => {
    if (!componentObj) {
        return 'Не указан';
    }
    if (componentObj.replacement_subscriber && componentObj.replacement_subscriber.user) {
        return `${componentObj.replacement_subscriber.user.first_name} ${componentObj.replacement_subscriber.user.last_name}`;
    }
    return 'Не указан';
};

class Profile extends React.Component {
    static contextType = UserSubscriberContext;

    constructor(props) {
        super(props);
        this.state = {
            subscriber: null,
            employee: null,
            vacations: null,
            vacationsPage: 1,
            vacationsPages: null,
            bonuses: null,
            bonusesPage: 1,
            bonusesPages: null,
            avatarImage: null,
            activeTab: 0,
            uploading: false,
            showAvatarModal: false,
            showChangePasswordModal: false,
            avatarPreview: null,
        };
        this.fileInputRef = React.createRef();
    }

    async componentDidMount() {
        await this.retrieveSubscriber();
        await this.retrieveEmployee();
        await this.retrieveVacations();
        await this.retrieveBonuses();
        await this.retrieveAvatar();
    }

    async componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps !== this.props) {
            await this.retrieveSubscriber();
            await this.retrieveEmployee();
            await this.retrieveVacations();
            await this.retrieveBonuses();
        }
    }

    async logout() {
        localStorage.removeItem('authTokens');
        this.props.navigate('/login');
    }

    async retrieveSubscriber() {
        this.setState({subscriber: null});
        const url = `subscriber/me`;
        const api = new BaseApi();
        let response = await api.get(url, {});
        this.setState({subscriber: response.data});
    }

    async retrieveEmployee() {
        this.setState({employee: null});
        const url = `employee/me`;
        const api = new BaseApi();
        let response = await api.get(url, {});
        this.setState({employee: response.data});
    }

    async retrieveAvatar() {
        if (this.context.subscriberObject?.id) {
            let image = await retrieveImage(
                'Subscriber',
                'image',
                this.context.subscriberObject.id
            );
            this.setState({avatarImage: image});
        }
    }

    async retrieveVacations(page = 1) {
        this.setState({vacations: null});
        const url = `vacation/my`;
        const params = {page: page};
        const api = new BaseApi();
        let response = await api.get(url, params);
        this.setState({
            vacations: response.data.results,
            vacationsPages: response.data.total_pages,
            vacationsPage: page,
        });
    }

    async retrieveBonuses(page = 1) {
        this.setState({bonuses: null});
        const url = `employee_bonus/my`;
        const params = {page: page};
        const api = new BaseApi();
        let response = await api.get(url, params);
        this.setState({
            bonuses: response.data.results,
            bonusesPages: response.data.total_pages,
            bonusesPage: page,
        });
    }

    handleAvatarClick() {
        this.setState({showAvatarModal: true});
    }

    handleAvatarChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Проверка размера файла (максимум 10MB)
        if (file.size > 10 * 1024 * 1024) {
            alert('Размер файла не должен превышать 10MB');
            return;
        }

        // Проверка типа файла
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            alert('Допустимые форматы: JPEG, PNG, GIF, WebP');
            return;
        }

        // Создаем превью
        const reader = new FileReader();
        reader.onloadend = () => {
            this.setState({avatarPreview: reader.result});
        };
        reader.readAsDataURL(file);

        // Загружаем файл
        await this.uploadAvatar(file);
    };

    uploadAvatar = async (file) => {
        this.setState({uploading: true});
        const api = new BaseApi();
        const formData = new FormData();
        formData.append('avatar', file);

        try {
            const response = await api.post('subscriber/update_avatar/', formData, true);
            if (response.status === 200) {
                // Обновляем изображение
                let image = await retrieveImage('Subscriber', 'image', this.context.subscriberObject.id);
                this.setState({
                    avatarImage: image,
                    avatarPreview: null,
                    showAvatarModal: false,
                    uploading: false
                });
            }
        } catch (error) {
            console.error('Ошибка при загрузке аватара:', error);
            alert('Ошибка при загрузке аватара');
            this.setState({uploading: false});
        }
    };

    removeAvatar = async () => {
        this.setState({uploading: true});
        const api = new BaseApi();
        
        try {
            const subscriber = this.context.subscriberObject;
            if (subscriber && subscriber.id) {
                const formData = new FormData();
                formData.append('image', ''); // Отправляем пустое значение для удаления
                
                const url = `subscriber/${subscriber.id}/`;
                const response = await api.patch(url, formData, true);
                
                if (response.status === 200) {
                    this.setState({
                        avatarImage: null,
                        showAvatarModal: false,
                        uploading: false
                    });
                }
            }
        } catch (error) {
            console.error('Ошибка при удалении аватара:', error);
            // Если нет метода удаления, просто очищаем превью
            this.setState({
                avatarPreview: null,
                uploading: false
            });
        }
    };

    getTabContent() {
        switch (this.state.activeTab) {
            case 0:
                return this.renderGeneralInfo();
            case 1:
                return this.renderBonuses();
            case 2:
                return this.renderVacations();
            case 3:
                return this.renderPermissions();
            case 4:
                return this.renderSecurity();
            default:
                return this.renderGeneralInfo();
        }
    }

    renderGeneralInfo() {
        const subscriber = this.state.subscriber;
        const employee = this.state.employee;
        const user = this.context.userObject;

        return (
            <Grid container spacing={2}>
                {/* Левая колонка - Аватар и действия */}
                <Grid xs={12} md={4}>
                    <Card variant="soft" sx={{textAlign: 'center', p: 2}}>
                        <Typography level="title-lg" sx={{mb: 2}}>
                            Профиль
                        </Typography>
                        
                        {/* Аватар */}
                        <Box sx={{position: 'relative', display: 'inline-block', mb: 2}}>
                            <Avatar
                                src={
                                    this.state.avatarPreview 
                                        ? this.state.avatarPreview 
                                        : this.state.avatarImage 
                                            ? `data:image/jpeg;base64,${this.state.avatarImage}` 
                                            : undefined
                                }
                                sx={{
                                    width: 120,
                                    height: 120,
                                    mx: 'auto',
                                    border: '3px solid',
                                    borderColor: 'primary.solidBg',
                                    '&:hover': {
                                        cursor: 'pointer',
                                        opacity: 0.8,
                                    },
                                }}
                                onClick={this.handleAvatarClick.bind(this)}
                            />
                            <Tooltip title="Изменить аватар">
                                <IconButton
                                    size="sm"
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 'calc(50% - 60px)',
                                        bgcolor: 'background.level1',
                                        '&:hover': {
                                            bgcolor: 'background.level2',
                                        },
                                    }}
                                    onClick={this.handleAvatarClick.bind(this)}
                                >
                                    <CameraAltIcon fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                        </Box>

                        {/* Статус */}
                        {subscriber && (
                            <Stack direction="row" spacing={1} justifyContent="center" sx={{mb: 2}}>
                                <Chip
                                    color={subscriber.active ? "success" : "danger"}
                                    variant="soft"
                                >
                                    {subscriber.active ? 'Активен' : 'Неактивен'}
                                </Chip>
                                {subscriber.external && (
                                    <Chip color="warning" variant="soft">
                                        Внешний
                                    </Chip>
                                )}
                            </Stack>
                        )}

                        {/* Типы подписчиков */}
                        {subscriber && subscriber.subscriber_type && subscriber.subscriber_type.length > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" sx={{mb: 2}}>
                                {subscriber.subscriber_type.map((type, index) => (
                                    <Chip key={index} variant="outlined" color="primary">
                                        {type.title}
                                    </Chip>
                                ))}
                            </Stack>
                        )}

                        {/* Контактная информация */}
                        <Stack spacing={1} sx={{mt: 2, textAlign: 'left', mb: 3}}>
                            {subscriber?.phone && (
                                <Box>
                                    <Typography level="body-xs" color="neutral">Телефон</Typography>
                                    <Typography level="body-md">{subscriber.phone}</Typography>
                                </Box>
                            )}
                            {subscriber?.academic_degree && (
                                <Box>
                                    <Typography level="body-xs" color="neutral">Ученая степень</Typography>
                                    <Typography level="body-md">{subscriber.academic_degree}</Typography>
                                </Box>
                            )}
                            {subscriber?.tg_id && (
                                <Box>
                                    <Typography level="body-xs" color="neutral">Telegram ID</Typography>
                                    <Typography level="body-md">@{subscriber.tg_id}</Typography>
                                </Box>
                            )}
                        </Stack>

                        {/* Кнопка выхода */}
                        <Divider sx={{my: 2}}/>
                        <Button
                            fullWidth
                            variant="soft"
                            color="danger"
                            startDecorator={<LogoutIcon/>}
                            onClick={this.logout.bind(this)}
                        >
                            Выйти
                        </Button>
                    </Card>
                </Grid>

                {/* Правая колонка - Данные */}
                <Grid xs={12} md={8}>
                    <CommonInfoTab
                        groups={[
                            {
                                title: 'Данные пользователя',
                                icon: <BadgeIcon/>,
                                fields: [
                                    {
                                        title: 'Логин',
                                        value: user?.username,
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                    {
                                        title: 'Фамилия',
                                        value: employee?.last_name || user?.last_name,
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                    {
                                        title: 'Имя',
                                        value: employee?.first_name || user?.first_name,
                                        html: true,
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                    {
                                        title: 'Отчество',
                                        value: employee?.patronymic || '',
                                        html: true,
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                    {
                                        title: 'ID пользователя',
                                        value: user?.id,
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                    {
                                        title: 'ID подписчика',
                                        value: subscriber?.id,
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                ]
                            },
                            {
                                title: 'Данные сотрудника',
                                icon: <WorkIcon/>,
                                fields: employee ? [
                                    {
                                        title: 'Должность',
                                        value: employee?.position || 'Не указана',
                                        xs: 12,
                                        sm: 12,
                                        md: 6,
                                        lg: 6,
                                    },
                                    // {
                                    //     title: 'Категория',
                                    //     value: employee?.category || 'Не указана',
                                    //     xs: 12,
                                    //     sm: 12,
                                    //     md: 6,
                                    //     lg: 6,
                                    // },
                                    // {
                                    //     title: 'Зарплата',
                                    //     value: employee?.salary ? `${employee.salary} руб.` : 'Не указана',
                                    //     xs: 12,
                                    //     sm: 12,
                                    //     md: 6,
                                    //     lg: 6,
                                    // },
                                ] : [
                                    {
                                        title: 'Нет данных сотрудника',
                                        value: null,
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }
                                ]
                            }
                        ]}
                    />
                </Grid>
            </Grid>
        );
    }

    renderBonuses() {
        return (
            <Card>
                <CardContent>
                    <CommonPaginatedTable
                        title={'Бонусы'}
                        data={this.state.bonuses}
                        pages={this.state.bonusesPages}
                        pageNumber={this.state.bonusesPage}
                        pagesCallback={this.retrieveBonuses.bind(this)}
                        fields={[
                            {
                                title: 'Сумма (до вычета НДФЛ)',
                                name: 'salary',
                                visible: true,
                                preProcess: (value) => value ? `${value} ₽` : 'N/A',
                            },
                            {
                                title: 'Дата выплаты',
                                name: 'pay_date',
                                visible: true,
                                preProcess: (value) => (value ? dayjs(value).format('DD MMM YYYY') : value),
                            },
                            {
                                title: 'Выплачено',
                                name: 'payed',
                                visible: true,
                                preProcess: (value) => value ? 'Да' : 'Нет',
                            },
                            {
                                title: 'Сделка',
                                name: 'opportunity.short_name',
                                linkPattern: '/opportunity/detail/:linkParam',
                                linkParam: 'opportunity.id',
                                linkTarget: '_blank',
                                visible: true,
                            },
                            {
                                title: 'Комментарий',
                                name: 'description',
                                visible: true,
                            },
                        ]}
                    />
                </CardContent>
            </Card>
        );
    }

    renderVacations() {
        return (
            <Card>
                <CardContent>
                    <CommonPaginatedTable
                        title={'Отпуска и больничные'}
                        data={this.state.vacations}
                        pages={this.state.vacationsPages}
                        pageNumber={this.state.vacationsPage}
                        pagesCallback={this.retrieveVacations.bind(this)}
                        extraComponents={[
                            // Можно добавить кнопку создания отпуска
                        ]}
                        fields={[
                            {
                                title: 'Тип',
                                name: 'xtype_human',
                                visible: true,
                                component: VacationTypeChip,
                            },
                            {
                                title: 'Дата начала',
                                name: 'start_date',
                                preProcess: (value) => (value ? dayjs(value).format('DD MMM YYYY') : value),
                                visible: true,
                            },
                            {
                                title: 'Дата окончания',
                                name: 'end_date',
                                preProcess: (value) => (value ? dayjs(value).format('DD MMM YYYY') : value),
                                visible: true,
                            },
                            {
                                title: 'Дней',
                                name: 'duration',
                                visible: true,
                                preProcess: (value) => value || 'N/A',
                            },
                            {
                                title: 'Замещающий',
                                name: 'replacement_subscriber',
                                visible: true,
                                component: ReplacementSubscriberDisplay,
                            },
                            {
                                title: 'Согласовано',
                                name: 'is_approved',
                                visible: true,
                                preProcess: (value) => value ? '✓ Да' : '✗ Нет',
                            },
                        ]}
                    />
                </CardContent>
            </Card>
        );
    }

    renderPermissions() {
        const subscriber = this.state.subscriber;
        
        if (!subscriber) {
            return <Typography>Нет данных</Typography>;
        }

        const permissions = subscriber.subscriber_type || [];
        const canSignLetters = subscriber.can_sign_letters;
        const canSignFinancial = subscriber.can_sign_financial;
        const isDeliverySuperuser = subscriber.delivery_superuser;
        const isRouteSuperuser = subscriber.route_superuser;

        return (
            <Card>
                <CardContent>
                    <Typography level="title-lg" sx={{mb: 2}}>
                        <StarIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                        Права доступа
                    </Typography>
                    
                    <Stack spacing={2}>
                        <Typography level="title-md">Типы подписчиков:</Typography>
                        {permissions.length > 0 ? (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                {permissions.map((type, index) => (
                                    <Chip 
                                        key={index} 
                                        variant="soft" 
                                        color="primary"
                                        size="lg"
                                    >
                                        {type.title}
                                    </Chip>
                                ))}
                            </Stack>
                        ) : (
                            <Typography level="body-md" color="neutral">
                                Нет типов подписчиков
                            </Typography>
                        )}

                        <Divider sx={{my: 1}}/>

                        <Typography level="title-md">Расширенные права:</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {canSignLetters && (
                                <Chip color="success" variant="soft">
                                    Подписание писем
                                </Chip>
                            )}
                            {canSignFinancial && (
                                <Chip color="success" variant="soft">
                                    Подписание финансовых документов
                                </Chip>
                            )}
                            {isDeliverySuperuser && (
                                <Chip color="warning" variant="soft">
                                    Суперпользователь БП
                                </Chip>
                            )}
                            {isRouteSuperuser && (
                                <Chip color="warning" variant="soft">
                                    Суперпользователь Маршрута
                                </Chip>
                            )}
                        </Stack>
                    </Stack>
                </CardContent>
            </Card>
        );
    }

    renderSecurity() {
        return (
            <Card>
                <CardContent>
                    <Typography level="title-lg" sx={{mb: 3}}>
                        <LockIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                        Безопасность
                    </Typography>
                    
                    <Stack spacing={3}>
                        {/* Смена пароля */}
                        <Box>
                            <Typography level="title-md" sx={{mb: 2}}>
                                Смена пароля
                            </Typography>
                            <Button
                                variant="solid"
                                color="primary"
                                startDecorator={<LockIcon/>}
                                onClick={() => this.setState({showChangePasswordModal: true})}
                            >
                                Изменить пароль
                            </Button>
                        </Box>

                        <Divider/>

                        {/* Информация о безопасности */}
                        <Stack spacing={2}>
                            <Typography level="title-md">Рекомендации по безопасности:</Typography>
                            <Stack spacing={1}>
                                <Typography level="body-sm">
                                    • Используйте пароль длиной не менее 8 символов
                                </Typography>
                                <Typography level="body-sm">
                                    • Используйте заглавные и строчные буквы
                                </Typography>
                                <Typography level="body-sm">
                                    • Используйте цифры и специальные символы
                                </Typography>
                                <Typography level="body-sm">
                                    • Не используйте один и тот же пароль несколько раз
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Modal для смены пароля */}
                    {this.state.showChangePasswordModal && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                bgcolor: 'rgba(0,0,0,0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                            }}
                            onClick={() => this.setState({showChangePasswordModal: false})}
                        >
                            <Box
                                sx={{
                                    width: '100%',
                                    maxWidth: 500,
                                    p: 2,
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <ChangePasswordModal 
                                    onClose={() => this.setState({showChangePasswordModal: false})}
                                />
                            </Box>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    }

    render() {
        if (!this.state.subscriber) {
            return <CircularProgress sx={{m: 'auto', display: 'flex'}}/>;
        }
        if (!this.state.employee) {
            return <CircularProgress sx={{m: 'auto', display: 'flex'}}/>;
        }
        if (this.state.vacations === null) {
            return <CircularProgress sx={{m: 'auto', display: 'flex'}}/>;
        }
        if (this.state.bonuses === null) {
            return <CircularProgress sx={{m: 'auto', display: 'flex'}}/>;
        }

        return (
            <UserSubscriberContext.Consumer>
                {({subscriberObject, userObject}) => (
                    <Stack spacing={2}>
                        {/* Tabs */}
                        <Card>
                            <Tabs defaultValue={0} value={this.state.activeTab} onChange={(event, newValue) => {
                                this.setState({activeTab: newValue});
                            }}>
                                <TabList>
                                    <Tab startDecorator={<InfoIcon/>}>Общая информация</Tab>
                                    <Tab startDecorator={<StarIcon/>}>Бонусы</Tab>
                                    <Tab startDecorator={<CalendarTodayIcon/>}>Отпуска</Tab>
                                    <Tab startDecorator={<BadgeIcon/>}>Права доступа</Tab>
                                    <Tab startDecorator={<LockIcon/>}>Безопасность</Tab>
                                </TabList>
                            </Tabs>
                            <CardContent sx={{pt: 3}}>
                                {this.getTabContent()}
                            </CardContent>
                        </Card>

                        {/* Avatar Modal */}
                        {this.state.showAvatarModal && (
                            <Box
                                sx={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1000,
                                }}
                                onClick={() => this.setState({showAvatarModal: false})}
                            >
                                <Card
                                    sx={{
                                        width: '400px',
                                        maxWidth: '90vw',
                                        p: 3,
                                        position: 'relative',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <Typography level="title-lg" sx={{mb: 2}}>
                                        Сменить аватар
                                    </Typography>
                                    
                                    {/* Preview */}
                                    <Box sx={{textAlign: 'center', mb: 2}}>
                                        <Avatar
                                            src={
                                                this.state.avatarPreview 
                                                    ? this.state.avatarPreview 
                                                    : this.state.avatarImage 
                                                        ? `data:image/jpeg;base64,${this.state.avatarImage}` 
                                                        : undefined
                                            }
                                            sx={{
                                                width: 150,
                                                height: 150,
                                                mx: 'auto',
                                                border: '3px solid',
                                                borderColor: 'primary.solidBg',
                                            }}
                                        />
                                    </Box>

                                    {/* Upload */}
                                    <input
                                        ref={this.fileInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/gif,image/webp"
                                        style={{display: 'none'}}
                                        onChange={this.handleAvatarChange}
                                    />
                                    
                                    <Stack spacing={2}>
                                        <Button
                                            fullWidth
                                            variant="solid"
                                            color="primary"
                                            startDecorator={<CameraAltIcon/>}
                                            onClick={() => this.fileInputRef.current.click()}
                                            disabled={this.state.uploading}
                                        >
                                            {this.state.uploading ? 'Загрузка...' : 'Выбрать файл'}
                                        </Button>
                                        
                                        {this.state.avatarImage && (
                                            <Button
                                                fullWidth
                                                variant="soft"
                                                color="danger"
                                                startDecorator={<DeleteIcon/>}
                                                onClick={this.removeAvatar}
                                                disabled={this.state.uploading}
                                            >
                                                Удалить аватар
                                            </Button>
                                        )}
                                        
                                        <Button
                                            fullWidth
                                            variant="plain"
                                            onClick={() => this.setState({showAvatarModal: false, avatarPreview: null})}
                                        >
                                            Отмена
                                        </Button>
                                    </Stack>
                                </Card>
                            </Box>
                        )}
                    </Stack>
                )}
            </UserSubscriberContext.Consumer>
        );
    }
}

export default withRouter(withNavigate(Profile));
